import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface ScrollPosition {
    x: number;
    y: number;
}

const MAX_POSITIONS = 20;

export function useScrollManager() {
    const location = useLocation();
    const scrollPositions = useRef<Map<string, ScrollPosition>>(new Map());
    const isNavigating = useRef(false);
    const isInitialMount = useRef(true);
    const prevPathname = useRef<string>(location.pathname);
    const isBackNavigation = useRef(false);

    // Store both x and y scroll positions
    const storeScrollPosition = useCallback((path: string) => {
        if (!isNavigating.current) {
            scrollPositions.current.set(path, {
                x: window.scrollX,
                y: window.scrollY
            });

            // Clean up old positions when limit is reached
            if (scrollPositions.current.size > MAX_POSITIONS) {
                const keys = Array.from(scrollPositions.current.keys());
                const keysToRemove = keys.slice(0, keys.length - MAX_POSITIONS);
                keysToRemove.forEach(key => scrollPositions.current.delete(key));
            }
        }
    }, []);

    // Restore scroll position or scroll to top based on navigation type
    const restoreScrollPosition = useCallback((path: string, shouldRestore: boolean) => {
        // Use requestAnimationFrame to ensure DOM is ready
        const restore = () => {
            // Only restore saved position when specifically requested (back/forward)
            if (shouldRestore) {
                const savedPosition = scrollPositions.current.get(path);
                if (savedPosition) {
                    window.scrollTo({
                        left: savedPosition.x,
                        top: savedPosition.y,
                    });
                }
            } else {
                // For new page navigation, always scroll to top
                window.scrollTo({
                    top: 0,
                    left: 0,
                });
            }
            isNavigating.current = false;
        };

        // Double RAF to ensure reliable scroll restoration after layout
        requestAnimationFrame(() => {
            requestAnimationFrame(restore);
        });
    }, []);

    // Handle popstate (browser back/forward) events
    const handlePopState = useCallback(() => {
        isBackNavigation.current = true;
        requestAnimationFrame(() => {
            restoreScrollPosition(window.location.pathname, true);
        });
    }, [restoreScrollPosition]);

    useEffect(() => {
        const currentPath = location.pathname;
        isNavigating.current = true;

        // Don't restore scroll on initial mount
        if (isInitialMount.current) {
            isInitialMount.current = false;
            isNavigating.current = false;
            return;
        }

        // Store current position before location changes
        const cleanup = () => {
            if (!isNavigating.current) {
                storeScrollPosition(prevPathname.current);
            }
        };

        // Listen for browser back/forward navigation
        window.addEventListener('popstate', handlePopState);

        // Restore scroll position after navigation or scroll to top
        // Use isBackNavigation.current to determine if this is a back/forward navigation
        restoreScrollPosition(currentPath, isBackNavigation.current);
        
        // Reset back navigation flag
        isBackNavigation.current = false;

        // Update previous pathname
        prevPathname.current = currentPath;

        return () => {
            cleanup();
            window.removeEventListener('popstate', handlePopState);
        };
    }, [location, handlePopState, storeScrollPosition, restoreScrollPosition]);

    // Expose methods for manual control
    return {
        saveScrollPosition: () => storeScrollPosition(location.pathname),
        //restoreScrollPosition: () => restoreScrollPosition(location.pathname, false)
    };
}